import type { ActivityLogicPlugin } from '../../core/types/plugin.base.types';
import type {
  PinpointHeatCell,
  PinpointPayload,
  PinpointRuntime,
} from '../../core/types/payloads/pinpoint.types';
import type { PluginContext } from '../../core/types/plugin.base.types';
import type { Participant, RoutingMode } from '../../core/types/room.types';
import type { HostActionPayload, StudentInputEnvelope } from '../../core/types/events.types';

const BUCKET_SIZE = 0.08;

function clamp01(v: number) {
  return Math.min(1, Math.max(0, v));
}

function bucketKey(x: number, y: number) {
  const bx = Math.floor(x / BUCKET_SIZE);
  const by = Math.floor(y / BUCKET_SIZE);
  return `${bx}:${by}`;
}

function rebuildHeatmap(taps: PinpointRuntime['taps']): PinpointHeatCell[] {
  const buckets = new Map<string, { x: number; y: number; count: number }>();
  for (const tap of Object.values(taps)) {
    const key = bucketKey(tap.x, tap.y);
    const bx = Math.floor(tap.x / BUCKET_SIZE);
    const by = Math.floor(tap.y / BUCKET_SIZE);
    const cx = bx * BUCKET_SIZE + BUCKET_SIZE / 2;
    const cy = by * BUCKET_SIZE + BUCKET_SIZE / 2;
    const existing = buckets.get(key);
    if (existing) existing.count += 1;
    else buckets.set(key, { x: cx, y: cy, count: 1 });
  }
  return Array.from(buckets.values());
}

export function validatePinpointPayload(raw: unknown): PinpointPayload {
  if (!raw || typeof raw !== 'object') throw new Error('Pinpoint payload must be an object');
  const data = raw as Partial<PinpointPayload>;
  if (!data.title) throw new Error('Pinpoint requires title');
  const imageUrl = String(data.imageUrl || '').trim();
  if (!imageUrl) throw new Error('Pinpoint requires imageUrl');
  const target = data.target
    ? {
        x: clamp01(Number(data.target.x)),
        y: clamp01(Number(data.target.y)),
        radius: Math.max(0.02, Number(data.target.radius) || 0.08),
      }
    : undefined;
  return {
    title: String(data.title),
    prompt: String(data.prompt || 'Toca el punto correcto en la imagen'),
    imageUrl,
    target,
  };
}

export function createInitialPinpointRuntime(
  _payload: PinpointPayload,
  _routing: RoutingMode,
): PinpointRuntime {
  return { taps: {}, heatmap: [], totalTaps: 0 };
}

export const pinpointLogic: ActivityLogicPlugin<'point'> = {
  mode: 'point',

  validatePayload: validatePinpointPayload,
  createInitialRuntime: createInitialPinpointRuntime,

  onStudentInput(ctx, input: StudentInputEnvelope, participant: Participant) {
    if (input.action !== 'TAP') {
      return { runtime: ctx.runtime };
    }
    if (ctx.phase !== 'ACTIVE') {
      return { runtime: ctx.runtime, rejected: true, rejectReason: 'NOT_ACTIVE' };
    }
    const data = input.data as { x?: number; y?: number } | undefined;
    const x = clamp01(Number(data?.x));
    const y = clamp01(Number(data?.y));
    if (!Number.isFinite(x) || !Number.isFinite(y)) {
      return { runtime: ctx.runtime, rejected: true, rejectReason: 'INVALID_TAP' };
    }

    const taps = {
      ...ctx.runtime.taps,
      [participant.participantId]: { x, y, ts: Date.now() },
    };
    const heatmap = rebuildHeatmap(taps);
    return {
      runtime: {
        taps,
        heatmap,
        totalTaps: Object.keys(taps).length,
      },
    };
  },

  onHostAction(ctx, action: HostActionPayload) {
    return { runtime: ctx.runtime };
  },
};

/** SVG data URL de mapa demo (Chile estilizado) */
export const DEMO_PINPOINT_MAP =
  'data:image/svg+xml,' +
  encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 600" fill="none">
    <rect width="400" height="600" fill="#e8f4fc"/>
    <path d="M200 40 L240 120 L230 200 L250 280 L220 360 L235 440 L200 520 L165 440 L180 360 L150 280 L170 200 L160 120 Z" fill="#94a3b8" stroke="#475569" stroke-width="2"/>
    <text x="200" y="30" text-anchor="middle" font-family="system-ui" font-size="14" fill="#334155">Mapa demo · zona genérica</text>
    <circle cx="200" cy="380" r="6" fill="#ef4444" opacity="0.3"/>
    <text x="200" y="560" text-anchor="middle" font-family="system-ui" font-size="11" fill="#64748b">Toca donde creas correcto</text>
  </svg>`);

export function demoPinpointPayload(): PinpointPayload {
  // Neutro — sin Patagonia/Chile (fuga geográfica vía defaultPayloadForMode).
  return {
    title: 'Ubica en el mapa',
    prompt: '¿Dónde queda la zona marcada en este mapa?',
    imageUrl: DEMO_PINPOINT_MAP,
    target: { x: 0.5, y: 0.63, radius: 0.12 },
  };
}

/** Partitura simplificada para Mapa de la Partitura */
export const DEMO_PARTITURA_SVG =
  'data:image/svg+xml,' +
  encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 800 220">
    <rect width="800" height="220" fill="#fffef8"/>
    <text x="24" y="28" font-family="Georgia,serif" font-size="16" fill="#334155">Mapa de la Partitura</text>
    <g stroke="#1e293b" stroke-width="1.2">
      <line x1="40" y1="60" x2="760" y2="60"/>
      <line x1="40" y1="75" x2="760" y2="75"/>
      <line x1="40" y1="90" x2="760" y2="90"/>
      <line x1="40" y1="105" x2="760" y2="105"/>
      <line x1="40" y1="120" x2="760" y2="120"/>
    </g>
    <text x="52" y="98" font-size="48" fill="#1e293b" font-family="serif">𝄞</text>
    <ellipse cx="200" cy="105" rx="9" ry="7" fill="#1e293b"/>
    <ellipse cx="280" cy="90" rx="9" ry="7" fill="#1e293b"/>
    <ellipse cx="360" cy="75" rx="9" ry="7" fill="#1e293b"/>
    <rect x="430" y="75" width="14" height="45" fill="#1e293b"/>
    <text x="480" y="98" font-size="14" fill="#64748b">𝄽 negra</text>
    <circle cx="620" cy="105" r="18" fill="none" stroke="#ef4444" stroke-width="2" stroke-dasharray="4 3" opacity="0.7"/>
    <text x="600" y="145" font-size="11" fill="#ef4444">¿Clave de sol?</text>
  </svg>`);

export function demoPartituraPayload(): PinpointPayload {
  return {
    title: 'Mapa de la Partitura',
    prompt: 'Toca la clave de sol en la partitura',
    imageUrl: DEMO_PARTITURA_SVG,
    target: { x: 0.09, y: 0.45, radius: 0.06 },
  };
}
